export const GPU_TIERS = [
  { count: 1, label: 'Good', desc: 'Prototyping', time: '~3.3 hr', cost: '~$0.50/hr', hourly: 0.50 },
  { count: 2, label: 'Better', desc: 'Research', time: '~1.8 hr', cost: '~$1.00/hr', recommended: true, hourly: 1.00 },
  { count: 4, label: 'Best', desc: 'Sweeps & deadlines', time: '~1.0 hr', cost: '~$2.00/hr', hourly: 2.00 },
] as const;

export const TRAINING_DEFAULTS = {
  num_envs: 8192,
  max_iterations: 5000,
  video_enabled: true,
  video_interval: 200,
  gpu_count: 2,
} as const;

export const SWEEPABLE_PARAMS = [
  { key: 'learning_rate', label: 'Learning Rate', type: 'float' as const, placeholder: 'e.g. 0.001' },
  { key: 'clip_param', label: 'Clip Param', type: 'float' as const, placeholder: 'e.g. 0.2' },
  { key: 'entropy_coef', label: 'Entropy Coef', type: 'float' as const, placeholder: 'e.g. 0.01' },
  { key: 'gamma', label: 'Gamma', type: 'float' as const, placeholder: 'e.g. 0.99' },
  { key: 'lam', label: 'Lambda', type: 'float' as const, placeholder: 'e.g. 0.95' },
  { key: 'num_learning_epochs', label: 'Learning Epochs', type: 'int' as const, placeholder: 'e.g. 5' },
  { key: 'num_mini_batches', label: 'Mini Batches', type: 'int' as const, placeholder: 'e.g. 4' },
  { key: 'desired_kl', label: 'Desired KL', type: 'float' as const, placeholder: 'e.g. 0.01' },
  { key: 'max_grad_norm', label: 'Max Grad Norm', type: 'float' as const, placeholder: 'e.g. 1.0' },
  { key: 'schedule', label: 'Schedule', type: 'select' as const, placeholder: '', options: ['adaptive', 'fixed'] },
] as const;

export const SWEEP_PRESETS = [
  {
    label: 'Quick LR Sweep',
    desc: '4 learning rates',
    fields: { learning_rate: ['1e-4', '3e-4', '1e-3', '3e-3'] } as Record<string, string[]>,
    seeds: 1,
  },
  {
    label: 'LR + Entropy',
    desc: '12 combinations',
    fields: {
      learning_rate: ['1e-4', '3e-4', '1e-3'],
      entropy_coef: ['0.005', '0.01', '0.05', '0.1'],
    } as Record<string, string[]>,
    seeds: 1,
  },
  {
    label: 'Publication Ready',
    desc: '10 seeds',
    fields: {} as Record<string, string[]>,
    seeds: 10,
  },
];

// Per-GPU hourly rates (USD) — must stay in sync with orchestrator/gpu_pricing.py
export const GPU_HOURLY_RATES: Record<string, number> = {
  'RTX 5090': 0.80,
  'RTX 4090': 0.50,
  'L40S': 0.31,
  'L40': 0.35,
  'RTX 6000 Ada': 0.60,
  'A6000': 0.80,
  'RTX 3090': 0.40,
  'RTX 4080': 0.35,
};

export const DEFAULT_GPU_HOURLY_RATE = 0.50;

export function getGpuHourlyRate(gpuType?: string): number {
  if (!gpuType) return DEFAULT_GPU_HOURLY_RATE;
  return GPU_HOURLY_RATES[gpuType] ?? DEFAULT_GPU_HOURLY_RATE;
}

// Heuristic cost model — derived from CLAUDE.md GPU benchmarks
export const COST_HEURISTIC = {
  base_iter_sec: 4.4,         // RTX 4090, 4096 envs, 1 GPU
  base_num_envs: 4096,
  gpu_hourly_rate: 0.50,      // $/hr per GPU fallback (RunPod spot RTX 4090)
  startup_overhead_min: 2,    // Isaac Sim boot + env init
  gpu_speedup: { 1: 1.0, 2: 1.7, 4: 2.9 } as Record<number, number>,
};

export interface CostEstimate {
  iter_sec: number;           // seconds per iteration
  task_time_hr: number;       // hours per task (including startup)
  task_cost: number;          // $ per task
  total_cost: number;         // $ for all tasks
  wall_time_hr: number;       // parallel wall time
  hourly_rate: number;        // $ per hour for this GPU config
}

export function estimateTrainingCost(
  num_envs: number,
  max_iterations: number,
  gpu_count: number,
  total_tasks: number,
  gpuType?: string,
): CostEstimate {
  const h = COST_HEURISTIC;
  const envs_factor = num_envs / h.base_num_envs;
  const gpu_speedup = h.gpu_speedup[gpu_count] ?? 1.0;
  const iter_sec = h.base_iter_sec * envs_factor / gpu_speedup;
  const task_time_hr = (max_iterations * iter_sec / 3600) + (h.startup_overhead_min / 60);
  const baseRate = getGpuHourlyRate(gpuType);
  const hourly_rate = gpu_count * baseRate;
  const task_cost = task_time_hr * hourly_rate;
  const total_cost = task_cost * total_tasks;
  const wall_time_hr = task_time_hr; // tasks run in parallel
  return { iter_sec, task_time_hr, task_cost, total_cost, wall_time_hr, hourly_rate };
}
